import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useState } from 'react'
import { ArrowLeft } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import RegistrationForm from '@/components/landing/registration-form'

import { campaign } from '@/constants/campaign'

const copy = campaign.register

const Register: FC = (): ReactNode => {
    // Once a registration is saved, the heading and intro step aside for the success message.
    const [submitted, setSubmitted] = useState(false)

    return (
        <Layout>
            <Seo title={copy.pageTitle} />

            <div className='relative isolate grid min-h-svh w-full grid-rows-[1fr_auto_1fr] justify-items-center overflow-clip px-5 py-[clamp(1.25rem,4svh,2.5rem)] selection:bg-primary selection:text-primary-foreground sm:px-8 lg:px-12'>
                <div aria-hidden='true' className='campaign-vignette pointer-events-none absolute inset-0 -z-10' />

                <section
                    id='register'
                    aria-labelledby={submitted ? undefined : 'register-title'}
                    aria-label={submitted ? copy.pageTitle : undefined}
                    className='relative row-start-2 flex w-full max-w-md flex-col items-center text-center'
                >
                    <div
                        aria-hidden='true'
                        className='pointer-events-none absolute top-1/2 left-1/2 -z-10 h-[min(90svh,50rem)] w-[min(135vw,70rem)] -translate-x-1/2 -translate-y-1/2'
                    >
                        <div className='campaign-glow size-full opacity-90 motion-safe:animate-glow-breathe' />
                    </div>

                    <Link
                        href='/'
                        className='group mb-8 inline-flex items-center gap-2 self-start rounded-sm font-heading text-xs font-semibold tracking-[0.24em] text-foreground/70 uppercase transition-colors hover:text-white focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white'
                    >
                        <ArrowLeft aria-hidden='true' className='size-4 transition-transform motion-safe:group-hover:-translate-x-0.5' />
                        {copy.backLink}
                    </Link>

                    {!submitted && (
                        <>
                            <h1
                                id='register-title'
                                className='text-balance pl-[0.06em] font-heading text-[length:clamp(2.25rem,9vw,3.75rem)] font-bold leading-none tracking-[0.06em] text-white'
                            >
                                {copy.heading}
                            </h1>

                            <p className='mt-4 max-w-sm text-balance text-base leading-relaxed text-foreground/80'>
                                {copy.intro}
                            </p>
                        </>
                    )}

                    <div className={submitted ? 'w-full' : 'mt-7 w-full sm:mt-8'}>
                        <RegistrationForm onSuccess={() => setSubmitted(true)} />
                    </div>
                </section>
            </div>
        </Layout>
    )
}

export default Register
